import React, { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Search, RefreshCw, Trash2, Dumbbell } from 'lucide-react';
import { toast } from 'sonner';
import { useAdmin } from './AdminContext';

export default function TrainingTab() {
  const { adminFetch, API_URL } = useAdmin();
  const [trainingSearchEmail, setTrainingSearchEmail] = useState('');
  const [userTrainingHistory, setUserTrainingHistory] = useState([]);
  const [loadingTraining, setLoadingTraining] = useState(false);
  const [selectedTrainingUser, setSelectedTrainingUser] = useState(null);

  const loadUserTrainingHistory = useCallback(async (email) => {
    if (!email) return;
    setLoadingTraining(true);
    try {
      const response = await adminFetch(`${API_URL}/api/v2/admin/user-training/${encodeURIComponent(email)}`);
      if (response.ok) {
        const data = await response.json();
        setUserTrainingHistory(data.workouts || []);
        setSelectedTrainingUser({
          email: data.user_email, userId: data.user_id,
          fromTrainingHistory: data.from_training_history,
          fromSyncData: data.from_sync_data, lastSynced: data.last_synced
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to load training history');
        setUserTrainingHistory([]);
      }
    } catch (error) {
      console.error('Error loading user training history:', error);
      toast.error('Network error loading training history');
    } finally {
      setLoadingTraining(false);
    }
  }, [adminFetch, API_URL]);

  const deleteDuplicateWorkouts = async (email, datePrefix) => {
    try {
      const response = await adminFetch(`${API_URL}/api/v2/admin/user-training/${encodeURIComponent(email)}/by-date/${datePrefix}`, { method: 'DELETE' });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        loadUserTrainingHistory(email);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete duplicates');
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  const deleteWorkoutEntry = async (email, entryId) => {
    try {
      const response = await adminFetch(`${API_URL}/api/v2/admin/user-training/${encodeURIComponent(email)}/${entryId}`, { method: 'DELETE' });
      if (response.ok) {
        toast.success('Entry deleted');
        loadUserTrainingHistory(email);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete entry');
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  return (
    <div className="space-y-4" data-testid="training-tab">
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Dumbbell className="h-5 w-5 text-green-400" />
            User Training History
          </CardTitle>
          <CardDescription className="text-slate-400">
            Search for a user by email to view and manage their workout history
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3 mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Enter user email..."
                value={trainingSearchEmail}
                onChange={(e) => setTrainingSearchEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && loadUserTrainingHistory(trainingSearchEmail)}
                className="pl-10 bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <Button
              onClick={() => loadUserTrainingHistory(trainingSearchEmail)}
              disabled={loadingTraining || !trainingSearchEmail}
              className="bg-green-600 hover:bg-green-500"
            >
              {loadingTraining ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              <span className="ml-2">Search</span>
            </Button>
          </div>

          {selectedTrainingUser && (
            <div className="space-y-4">
              <div className="bg-slate-700/50 rounded-lg p-4">
                <p className="text-white font-medium">{selectedTrainingUser.email}</p>
                <p className="text-slate-400 text-sm">User ID: {selectedTrainingUser.userId}</p>
                <p className="text-slate-400 text-sm">{userTrainingHistory.length} workouts found</p>
                {selectedTrainingUser.fromTrainingHistory !== undefined && (
                  <p className="text-slate-500 text-xs mt-1">
                    Sources: {selectedTrainingUser.fromTrainingHistory} from DB, {selectedTrainingUser.fromSyncData} from sync
                    {selectedTrainingUser.lastSynced && ` | Last sync: ${new Date(selectedTrainingUser.lastSynced).toLocaleString()}`}
                  </p>
                )}
              </div>

              {(() => {
                const grouped = {};
                userTrainingHistory.forEach(w => {
                  const dateKey = w.date?.substring(0, 10) || 'Unknown';
                  if (!grouped[dateKey]) grouped[dateKey] = [];
                  grouped[dateKey].push(w);
                });
                return Object.entries(grouped).map(([date, workouts]) => (
                  <div key={date} className="bg-slate-700/30 rounded-lg p-4 mb-3">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className="text-white font-medium">{date}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${workouts.length > 1 ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>
                          {workouts.length} workout{workouts.length > 1 ? 's' : ''}
                        </span>
                        {workouts.length > 1 && <span className="text-xs text-red-400">DUPLICATE</span>}
                      </div>
                      {workouts.length > 1 && (
                        <Button size="sm" variant="destructive" onClick={() => deleteDuplicateWorkouts(selectedTrainingUser.email, date)} className="bg-red-600 hover:bg-red-500 text-xs">
                          <Trash2 className="h-3 w-3 mr-1" />Delete Duplicates
                        </Button>
                      )}
                    </div>
                    <div className="space-y-2">
                      {workouts.map((workout, idx) => (
                        <div key={workout.entry_id || idx} className="bg-slate-800/50 rounded p-3 flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-slate-300 text-sm">{workout.day_key || workout.dayKey || 'Unknown Day'} - Week {workout.week || '?'}</span>
                              <span className="text-slate-500 text-xs">{new Date(workout.date).toLocaleTimeString()}</span>
                            </div>
                            <div className="text-xs text-slate-400">
                              {workout.exercises?.length || 0} exercises
                              {workout.exercises?.slice(0, 3).map((ex, i) => (
                                <span key={i} className="ml-2 text-slate-500">- {ex.name}</span>
                              ))}
                              {(workout.exercises?.length || 0) > 3 && <span className="text-slate-500"> +{workout.exercises.length - 3} more</span>}
                            </div>
                          </div>
                          {workout.entry_id && (
                            <Button size="sm" variant="ghost" onClick={() => deleteWorkoutEntry(selectedTrainingUser.email, workout.entry_id)} className="text-red-400 hover:text-red-300 hover:bg-red-500/20">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ));
              })()}

              {userTrainingHistory.length === 0 && (
                <div className="text-center py-8 text-slate-400">No training history found for this user</div>
              )}
            </div>
          )}

          {!selectedTrainingUser && !loadingTraining && (
            <div className="text-center py-8 text-slate-400">Enter an email address to search for user training history</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
